(() => {
  // ---- Environment guards
  const desktopMQ = window.matchMedia('(min-width: 990px)');
  const canHover  = window.matchMedia('(hover: hover) and (pointer: fine)');
  const ACTIVE = () => desktopMQ.matches && canHover.matches;

  const CLOSE_DELAY = 220;     // time to "bridge the gap" into a submenu
  const BRIDGE_PX   = 10;      // invisible buffer between parent and submenu

  // ---- Small helpers
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const measure = (el) => {
    if (!el) return { w: 0, h: 0 };
    const prevDisplay = el.style.display;
    const prevVis     = el.style.visibility;
    el.style.display = 'block';
    el.style.visibility = 'hidden';
    const out = { w: el.scrollWidth, h: el.scrollHeight };
    el.style.display = prevDisplay;
    el.style.visibility = prevVis;
    return out;
  };

  function maybeFlip(details, panel) {
    if (!panel) return;
    details.classList.remove('flip-left');
    const rect   = details.getBoundingClientRect();
    const { w }  = measure(panel);
    const vw     = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const spaceR = vw - rect.right;
    if (spaceR < w + 8) details.classList.add('flip-left');
  }

  function maybeBump(details, panel) {
    if (!panel) return;
    panel.style.top = '';
    details.classList.remove('bump-up');

    const rect  = details.getBoundingClientRect();
    const { h } = measure(panel);
    const vh    = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const overflow = (rect.top + h) - vh;

    if (overflow > 0) {
      const bump = Math.min(overflow + 8, rect.top);
      panel.style.top = `-${bump}px`;
      details.classList.add('bump-up');
    }
  }

  function openDetail(details, panel) {
    if (!ACTIVE()) return;
    clearTimeout(details._hoverTimer);
    if (!details.hasAttribute('open')) details.setAttribute('open', '');
    details.classList.add('menu-opening');
    maybeFlip(details, panel);
    maybeBump(details, panel);
  }

  function scheduleClose(details) {
    if (!ACTIVE()) return;
    clearTimeout(details._hoverTimer);
    details._hoverTimer = setTimeout(() => {
      details.classList.remove('menu-opening', 'flip-left', 'bump-up');
      details.removeAttribute('open');
    }, CLOSE_DELAY);
  }

  // ---- Intercept Dawn's click-toggle on desktop (capture phase)
  function interceptClicks(drawerRoot) {
    // Intercept *all* summary clicks inside the drawer on desktop
    $$ (drawerRoot, 'summary').forEach((summary) => {
      summary.addEventListener('click', (e) => {
        if (!ACTIVE()) return;
        // We want hover to control open/close; click should not toggle on desktop.
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
      }, true /* capture */);
    });
  }

  // ---- Build an invisible "bridge" so cursor doesn't fall into a gap
  function ensureBridge(panel, flippedLeft) {
    if (!panel) return;
    let bridge = panel.querySelector(':scope > .__hover-bridge');
    if (!bridge) {
      bridge = document.createElement('div');
      bridge.className = '__hover-bridge';
      Object.assign(bridge.style, {
        position: 'absolute',
        top: '0',
        width: `${BRIDGE_PX}px`,
        height: '100%',
        content: "''",
        zIndex: '1',
        pointerEvents: 'auto'
      });
      panel.appendChild(bridge);
    }
    bridge.style.left  = flippedLeft ? 'auto' : `-${BRIDGE_PX}px`;
    bridge.style.right = flippedLeft ? `-${BRIDGE_PX}px` : 'auto';
  }

  // ---- Bind hover to a <details> row
  function bindRow(details) {
    const summary = details.querySelector(':scope > summary');
    const panel   = details.querySelector(':scope > .menu-drawer__submenu');
    details._hoverTimer = null;

    // Keep submenu "hot"
    const enter = () => {
      openDetail(details, panel);
      ensureBridge(panel, details.classList.contains('flip-left'));
    };
    const leave = () => scheduleClose(details);

    // Hover on whole row is more forgiving near edges
    details.addEventListener('mouseenter', enter);
    details.addEventListener('mouseleave', leave);

    // Also keep the panel itself hot
    if (panel) {
      panel.addEventListener('mouseenter', enter);
      panel.addEventListener('mouseleave', leave);
    }

    // Defensive: if summary is clicked on desktop, do nothing (handled by capture above)
    if (summary) {
      summary.addEventListener('keydown', (e) => {
        // Let keyboard users open with Enter/Space even on desktop
        if (!ACTIVE()) return;
        if (e.code === 'Enter' || e.code === 'Space') {
          e.preventDefault();
          openDetail(details, panel);
        }
      });
    }
  }

  // ---- Keep the root drawer column open on hover without focus-trap/scroll-lock
  function bindRootHover(headerDrawer) {
    const rootDetails = headerDrawer.mainDetailsToggle || headerDrawer.querySelector(':scope > details');
    if (!rootDetails) return;

    const rootSummary = rootDetails.querySelector(':scope > summary');
    const rootPanel   = rootDetails.querySelector(':scope > .menu-drawer');

    const openRoot = () => {
      if (!ACTIVE()) return;
      clearTimeout(rootDetails._hoverTimer);
      if (!rootDetails.hasAttribute('open')) rootDetails.setAttribute('open', '');
      rootDetails.classList.add('menu-opening');
    };
    const closeRoot = () => {
      if (!ACTIVE()) return;
      clearTimeout(rootDetails._hoverTimer);
      rootDetails._hoverTimer = setTimeout(() => {
        rootDetails.classList.remove('menu-opening');
        rootDetails.removeAttribute('open');
      }, CLOSE_DELAY);
    };

    if (rootSummary) {
      rootSummary.addEventListener('mouseenter', openRoot);
      rootSummary.addEventListener('mouseleave', closeRoot);
      // Click on desktop should not toggle; capture handler already prevents it.
    }
    if (rootPanel) {
      rootPanel.addEventListener('mouseenter', openRoot);
      rootPanel.addEventListener('mouseleave', closeRoot);
    }
  }

  // ---- Bind all current and future rows
  function bindAll(headerDrawer) {
    interceptClicks(headerDrawer);
    bindRootHover(headerDrawer);
    $$ (headerDrawer, '.menu-drawer details').forEach(bindRow);
  }

  // ---- Rebind on DOM mutations (menu editor / localization can re-render)
  function observe(headerDrawer) {
    const obs = new MutationObserver((muts) => {
      if (!ACTIVE()) return;
      // Re-bind only added nodes with <details>
      muts.forEach((m) => {
        m.addedNodes && m.addedNodes.forEach((n) => {
          if (!(n instanceof Element)) return;
          if (n.matches?.('.menu-drawer details')) bindRow(n);
          $$ (n, '.menu-drawer details').forEach(bindRow);
        });
      });
    });
    obs.observe(headerDrawer, { childList: true, subtree: true });
  }

  // ---- Init when DOM is ready
  function init() {
    const headerDrawer = document.querySelector('header-drawer');
    if (!headerDrawer) return;

    // Avoid double-init
    if (headerDrawer.__hoverMenuInstalled) return;
    headerDrawer.__hoverMenuInstalled = true;

    bindAll(headerDrawer);
    observe(headerDrawer);

    // Re-evaluate on viewport or hover-capability changes
    desktopMQ.addEventListener?.('change', () => { /* noop; capture guards handle behavior */ });
    canHover.addEventListener?.('change',   () => { /* noop */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
